/**
 * Pre-migration snapshot and grandfather-count helpers for unified favorites rollout.
 * Additive only — not wired into app code until a later phase.
 */
import { createFavorite, listFavorites } from '@/lib/favoritesApi';
import {
  CLOUD_CACHE_KEY,
  CLOUD_MIGRATED_KEY,
  LOCAL_V2_KEY,
  RUTY_FAVORITES_KEY,
  RUTY_MIGRATED_KEY,
  isCloudMigrated,
  normalizeFavorites,
  normalizeSambandSlug,
  readStoredLocalRoutesRaw,
  markCloudMigratedNow,
  resolveLegacySambandSlug,
  type FerryFavorite,
} from '@/lib/favoritesStore';
import { trackEvent } from '@/lib/analytics';
import { getAnonUserId } from '@/lib/userIdentity';
import { supabase } from '@/integrations/supabase/client';

export const MIGRATION_BACKUP_KEY = 'favorites_migration_backup_v1';
export const GRANDFATHER_COUNT_KEY = 'favorites_grandfather_count_v1';

export interface MigrationBackup {
  localV2: unknown;
  cloudCache: unknown;
  ruty: unknown;
  migratedFlags: {
    legacyCloud: boolean;
    ruty: boolean;
  };
  anonUserId: string;
  timestamp: string;
}

function readRawJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readMigratedFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** Returns parsed backup or null when missing or corrupt. */
export function readMigrationBackup(): MigrationBackup | null {
  try {
    const raw = localStorage.getItem(MIGRATION_BACKUP_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as MigrationBackup;
  } catch {
    return null;
  }
}

/**
 * Snapshots current favorites localStorage into MIGRATION_BACKUP_KEY.
 * First snapshot wins — never overwrites an existing backup.
 * Returns null when persistence fails so callers can abort migration.
 */
export function writeMigrationBackup(): MigrationBackup | null {
  const existing = readMigrationBackup();
  if (existing) return existing;

  const backup: MigrationBackup = {
    localV2: readRawJson(LOCAL_V2_KEY),
    cloudCache: readRawJson(CLOUD_CACHE_KEY),
    ruty: readRawJson(RUTY_FAVORITES_KEY),
    migratedFlags: {
      legacyCloud: readMigratedFlag(CLOUD_MIGRATED_KEY),
      ruty: readMigratedFlag(RUTY_MIGRATED_KEY),
    },
    anonUserId: getAnonUserId(),
    timestamp: new Date().toISOString(),
  };

  try {
    localStorage.setItem(MIGRATION_BACKUP_KEY, JSON.stringify(backup));
    const verified = readMigrationBackup();
    if (!verified) return null;
    return verified;
  } catch {
    return null;
  }
}

export function readGrandfatherCount(): number | null {
  try {
    const raw = localStorage.getItem(GRANDFATHER_COUNT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeGrandfatherCount(n: number): void {
  try {
    localStorage.setItem(GRANDFATHER_COUNT_KEY, JSON.stringify(n));
  } catch { /* ignore quota */ }
}

export type Phase2MigrationAborted = {
  status: 'aborted';
  reason: 'backup_failed';
};

export type Phase2MigrationConfirmed = {
  status: 'confirmed';
  migrated: number;
  cloudCount: number;
};

export type Phase2MigrationFailed = {
  status: 'failed';
  missing: string[];
};

export type Phase2MigrationSkipped = {
  status: 'skipped';
};

export type Phase2MigrationSkippedEmpty = {
  status: 'skipped_empty';
};

export type Phase2MigrationResult =
  | Phase2MigrationAborted
  | Phase2MigrationConfirmed
  | Phase2MigrationFailed
  | Phase2MigrationSkipped
  | Phase2MigrationSkippedEmpty;

function cloudSlugSet(favorites: FerryFavorite[]): Set<string> {
  return new Set(favorites.map((favorite) => normalizeSambandSlug(favorite.samband_slug)));
}

/** Analytics must never block or alter migration outcome. trackEvent does not swallow errors. */
function safeTrackMigration(params: Record<string, string | number | boolean>): void {
  try {
    trackEvent('favorites_migration', params);
  } catch {
    /* ignore */
  }

  try {
    let anonUserId: string | null = null;
    try {
      anonUserId = getAnonUserId();
    } catch {
      /* ignore */
    }

    supabase
      .from('analytics_events')
      .insert({
        event_name: 'favorites_migration',
        outcome: params.outcome as string,
        payload: params,
        anon_user_id: anonUserId,
      })
      .then(({ error }) => {
        if (error) console.warn('[analytics] supabase insert failed', error.message);
      });
  } catch {
    /* ignore */
  }
}

async function fetchCloudFavoritesForMigration(anonUserId: string): Promise<FerryFavorite[]> {
  const data = await listFavorites(anonUserId);
  return normalizeFavorites(data.favorites);
}

/**
 * Phase 2b: upload local-only v2 routes to cloud, confirm round-trip, then set migrated flag.
 * Does not modify local v2 data. Not wired into app code until a later phase.
 */
export async function runPhase2Migration(anonUserId: string): Promise<Phase2MigrationResult> {
  if (isCloudMigrated()) {
    return { status: 'skipped' };
  }

  const localRoutes = readStoredLocalRoutesRaw();
  const localSlugs = [
    ...new Set(
      localRoutes
        .map((route) => resolveLegacySambandSlug(route))
        .filter((slug) => slug.length > 0),
    ),
  ];

  if (localSlugs.length === 0) {
    try {
      markCloudMigratedNow();
    } catch {
      console.warn('[favoritesMigration] migrated flag persist failed — migration will re-run next mount (harmless, idempotent)');
    }
    return { status: 'skipped_empty' };
  }

  const backup = writeMigrationBackup();
  if (!backup) {
    console.warn('[favoritesMigration] Phase 2 migration aborted: backup failed');
    safeTrackMigration({ outcome: 'aborted', reason: 'backup_failed' });
    return { status: 'aborted', reason: 'backup_failed' };
  }

  let cloudFavorites: FerryFavorite[];
  try {
    cloudFavorites = await fetchCloudFavoritesForMigration(anonUserId);
  } catch (err) {
    console.warn('[favoritesMigration] Phase 2 migration failed: initial cloud fetch', err);
    const missing = [...localSlugs];
    safeTrackMigration({ outcome: 'failed', missingCount: missing.length });
    return { status: 'failed', missing };
  }

  const initialCloudSlugs = cloudSlugSet(cloudFavorites);
  const localOnlySlugs = localSlugs.filter((slug) => !initialCloudSlugs.has(slug));

  let migrated = 0;
  for (let i = 0; i < localOnlySlugs.length; i++) {
    const slug = localOnlySlugs[i];
    try {
      await createFavorite(
        {
          samband_slug: slug,
          position: cloudFavorites.length + i,
        },
        anonUserId,
      );
      migrated += 1;
    } catch (err) {
      console.warn('[favoritesMigration] Phase 2 migration failed: POST', slug, err);
      const missing = localOnlySlugs.slice(i);
      safeTrackMigration({ outcome: 'failed', missingCount: missing.length });
      return { status: 'failed', missing };
    }
  }

  let confirmedCloud: FerryFavorite[];
  try {
    confirmedCloud = await fetchCloudFavoritesForMigration(anonUserId);
  } catch (err) {
    console.warn('[favoritesMigration] Phase 2 migration failed: confirmation fetch', err);
    const missing = [...localSlugs];
    safeTrackMigration({ outcome: 'failed', missingCount: missing.length });
    return { status: 'failed', missing };
  }

  const confirmedCloudSlugs = cloudSlugSet(confirmedCloud);
  const missing = localSlugs.filter((slug) => !confirmedCloudSlugs.has(slug));
  if (missing.length > 0) {
    console.warn('[favoritesMigration] Phase 2 migration failed: missing slugs after confirmation', missing);
    safeTrackMigration({ outcome: 'failed', missingCount: missing.length });
    return { status: 'failed', missing };
  }

  if (readGrandfatherCount() === null) {
    writeGrandfatherCount(confirmedCloud.length);
  }
  try {
    markCloudMigratedNow();
  } catch {
    console.warn('[favoritesMigration] migrated flag persist failed — migration will re-run next mount (harmless, idempotent)');
  }

  const result: Phase2MigrationConfirmed = {
    status: 'confirmed',
    migrated,
    cloudCount: confirmedCloud.length,
  };
  safeTrackMigration({ outcome: 'confirmed', migrated, cloudCount: result.cloudCount });
  return result;
}
